/**
 * Communications View — Public End-User Page
 * 
 * Displays active communication messages for a scanned tag.
 * Fetched from the public endpoint: GET /api/communications/active/{org_id}/{card_id}
 * Sorted by: Emergency > Priority > Date
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, AlertTriangle, ExternalLink, Megaphone, ArrowLeft, Clock } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_ACCENT = {
  critical: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', badge: 'bg-red-600 text-white' },
  high: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', badge: 'bg-orange-500 text-white' },
  normal: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-500 text-white' },
  low: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', badge: 'bg-gray-400 text-white' },
};

export default function CommunicationsView() {
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();
  const cardId = searchParams.get('card') || '';
  const returnUrl = searchParams.get('return') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [commsEnabled, setCommsEnabled] = useState(false);
  const [orgLogo, setOrgLogo] = useState(null);
  const [orgName, setOrgName] = useState('');

  useEffect(() => {
    fetchMessages();
  }, [orgId, cardId]);

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API}/api/communications/active/${orgId}/${cardId}`);
      if (!res.ok) throw new Error('Unable to load communications');
      const data = await res.json();
      setCommsEnabled(data.comms_enabled);
      setMessages(data.messages || []);
    } catch (err) {
      setError(err.message);
    }

    // Also fetch org branding for logo/name
    try {
      const surveyRes = await fetch(`${API}/api/hotels-standalone/retailer-alert-survey/${orgId}/none?card=${cardId}`);
      if (surveyRes.ok) {
        const surveyData = await surveyRes.json();
        setOrgName(surveyData.organization?.name || '');
        if (surveyData.branding?.logo_url) {
          const url = surveyData.branding.logo_url;
          setOrgLogo(url.startsWith('http') ? url : `${API}${url}`);
        }
      }
    } catch { /* non-critical */ }

    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-600 to-teal-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl">
          <CardContent className="pt-6 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
            <span className="ml-3 text-gray-600">Loading communications...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl">
          <CardContent className="pt-6 text-center">
            <p className="text-red-600 text-lg font-medium mb-2">Error</p>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!commsEnabled || messages.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-600 to-teal-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl" data-testid="comms-no-messages">
          <CardContent className="pt-8 pb-8 text-center">
            {orgLogo && <img src={orgLogo} alt="Logo" className="h-12 object-contain mx-auto mb-4" />}
            <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">No Active Communications</h1>
            <p className="text-gray-500 text-sm">There are no messages at this time. Check back later.</p>
            {returnUrl && (
              <Button variant="outline" className="mt-6" onClick={() => window.location.href = returnUrl} data-testid="comms-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-600 to-teal-800 p-4" data-testid="comms-view-page">
      <div className="max-w-md mx-auto space-y-4">
        {/* Header */}
        <div className="text-center pt-4 pb-2">
          {orgLogo && <img src={orgLogo} alt="Logo" className="h-12 object-contain mx-auto mb-3" />}
          {orgName && <p className="text-white/80 text-sm font-medium">{orgName}</p>}
          <h1 className="text-white text-xl font-bold flex items-center justify-center gap-2">
            <Megaphone className="w-5 h-5" /> Communications
          </h1>
        </div>

        {/* Messages */}
        {messages.map((msg, idx) => {
          const style = PRIORITY_ACCENT[msg.priority] || PRIORITY_ACCENT.normal;
          return (
            <Card
              key={msg.id || idx}
              className={`bg-white shadow-xl overflow-hidden ${msg.is_emergency ? 'ring-2 ring-red-500' : ''}`}
              data-testid={`comms-message-${msg.id || idx}`}
            >
              {/* Emergency banner */}
              {msg.is_emergency && (
                <div className="bg-red-600 text-white px-4 py-2 flex items-center gap-2" data-testid="emergency-banner">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-bold uppercase tracking-wide">Emergency Message</span>
                </div>
              )}

              {/* Image */}
              {msg.resolved_image_url && (
                <img
                  src={msg.resolved_image_url}
                  alt=""
                  className="w-full h-44 object-cover"
                  data-testid={`comms-image-${msg.id || idx}`}
                />
              )}

              <CardContent className="pt-4 pb-5 px-5">
                {/* Priority badge */}
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${style.badge}`}>
                    {msg.priority?.toUpperCase()}
                  </span>
                  {msg.end_date && (
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Clock className="w-3 h-3" /> Expires {new Date(msg.end_date).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h2 className="text-lg font-bold text-gray-900 mb-1">{msg.title}</h2>

                {/* Description */}
                {msg.description && (
                  <p className="text-gray-600 text-sm leading-relaxed mb-3">{msg.description}</p>
                )}

                {/* Price */}
                {msg.price && (
                  <p className="text-2xl font-bold text-teal-600 mb-2" data-testid={`comms-price-${msg.id || idx}`}>
                    {msg.price}
                  </p>
                )}

                {/* Link */}
                {msg.link_url && (
                  <a
                    href={msg.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-teal-600 text-sm font-medium hover:underline"
                    data-testid={`comms-link-${msg.id || idx}`}
                  >
                    <ExternalLink className="w-4 h-4" /> Learn More
                  </a>
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* Back button */}
        {returnUrl && (
          <div className="text-center pb-6">
            <Button
              variant="outline"
              className="bg-white/10 text-white border-white/30 hover:bg-white/20"
              onClick={() => window.location.href = returnUrl}
              data-testid="comms-view-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
